import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { Link } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { addDays, endOfMonth } from 'date-fns';
import _ from 'lodash';

import {
  GlobalsContext,
  getCookie,
  timeout,
} from '@bizapp-frontend/customer/globals';
import {
  FormComponentProps,
  initFormDataPairs,
} from '@bizapp-frontend/customer/organisms/form/FormGenerator';
import { PriceInfo } from '@bizapp-frontend/customer/organisms/form/PriceTable';
import { ProcessingDialogConfig } from '@bizapp-frontend/customer/organisms/ProcessingDialog';
import {
  serviceIcon,
  serviceName,
} from '@bizapp-frontend/customer/templates/service-detail';
import { PurchaseApplicationContext } from '@bizapp-frontend/customer/templates/form/PurchaseApplication';
import {
  PurchaseApplicationStep1,
  PurchaseApplicationStep1Props,
} from '@bizapp-frontend/customer/templates/form/PurchaseApplicationStep1';
import { PurchaseApplicationCommonProps } from '@bizapp-frontend/customer/pages/form/pjb/PurchaseApplication';
import { Usage, UsageInfo } from '@bizapp-frontend/customer/pages/Home';

export interface PurchaseFormStep1Props
  extends Omit<PurchaseApplicationStep1Props, 'priceInfo'>,
    PurchaseApplicationCommonProps {
  applicationControlAPIBaseUrl: string;
  zipcodeAPIBaseUrl: string;
  feeCalculatorAPIBaseUrl: string;
  customerAPIBaseUrl: string;
  tenantManagementAPIBaseUrl: string;
  pjbDomainUrlSuffix: string;
}

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    submitButtonArea: {
      width: '100%',
      margin: theme.spacing(4, 0, 0),
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'center',
    },
    agreement: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: theme.spacing(5.5),
    },
    submitButton: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: theme.spacing(5),
      '& > button': {
        width: '100%',
        height: '48px',
        maxWidth: '400px',
      },
      '& span': {
        display: 'block',
      },
    },
    pjbLink: {
      display: 'inline',
      margin: theme.spacing(0),
      color: '#1565C0',
      fontSize: '14px',
      lineHeight: '21px',
      cursor: 'pointer',
    },
    backlink: {
      display: 'block',
      textAlign: 'center',
      margin: theme.spacing(2, 'auto', 0),
      color: '#1565C0',
      fontSize: '14px',
      lineHeight: '21px',
      cursor: 'pointer',
    },
  }),
);

type FormData = {
  [key: string]: boolean | string | undefined;
};

interface CustomerCompany {
  customerId: string;
  companyName: string;
  companyNameKana: string;
  zipCode: string;
  prefecture: string;
  address: string;
  tel: string;
  timestamp: number;
  customerKind: string;
}

interface CustomerUser {
  userId: string;
  userLastName: string;
  userFirstName: string;
  userName: string;
  userLastNameKana: string;
  userFirstNameKana: string;
  userNameKana: string;
  email: string;
  jobTitle: string;
  tel: string;
  timestamp: number;
  department: string;
}
interface CustomerInfo {
  company: CustomerCompany;
  users: CustomerUser[];
}

export const PurchaseFormStep1: React.FC<PurchaseFormStep1Props> = ({
  applicationControlAPIBaseUrl,
  zipcodeAPIBaseUrl,
  feeCalculatorAPIBaseUrl,
  customerAPIBaseUrl,
  tenantManagementAPIBaseUrl,
  pjbDomainUrlSuffix,
  tenantId,
  serviceId,
  planId,
  customerId,
  emailAddress,
  userId,
  customerKind,
  ...props
}) => {
  const classes = useStyle();
  const [warning, setWarning] = React.useState(false);
  const [licenseNumber, setLicenseNumber] = React.useState('20');
  const [price, setPrice] = React.useState<PriceInfo>();
  const [submitForm, setSubmitForm] = React.useState(false);
  const [disableSubmit, setDisableSubmit] = React.useState(false);

  const [
    dialogConfig,
    setDialogConfig,
  ] = React.useState<ProcessingDialogConfig>({
    state: 'close',
  });

  const [initDataLoaded, setInitDataLoaded] = React.useState(false);

  const navigate = useNavigate();

  const { step, setStep } = React.useContext(PurchaseApplicationContext);
  const { state: globalState } = React.useContext(GlobalsContext);
  const { getAccessTokenSilently } = useAuth0();

  const handleDialogClick = () => {
    setWaitingStates({});
    setDialogConfig({ state: 'close' });
  };

  const { silentLoadSessionData } = React.useContext(
    PurchaseApplicationContext,
  );

  const [loadSessionData, setLoadSessionData] = React.useState<
    boolean | undefined
  >(undefined);
  const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(
    false,
  );
  const [sessionData, setSessionData] = React.useState<FormData | null>(null);

  React.useEffect(() => {
    if (customerKind === 'personal') {
      setIsValids((prev) => ({
        ...prev,
        'select-prefecture': true,
      }));
    }
  }, [customerKind]);

  React.useEffect(() => {
    if (sessionData) {
      if (_.isEmpty(sessionData)) {
        setLoadSessionData(false);
      } else if (silentLoadSessionData) {
        setLoadSessionData(true);
      } else {
        setShowConfirmationDialog(true);
      }
    }
  }, [sessionData, silentLoadSessionData]);

  const [hasSubmittedDraft, setHasSubmittedDraft] = React.useState(false);
  const [draftId, setDraftId] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    if (customerId && tenantId) {
      const getDraftById = async (draftId: string) => {
        // console.debug('get draft by cookie id');
        const accessToken = await getAccessTokenSilently();
        const method = 'GET';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/draft/customers/${customerId}/drafts/${draftId}?draft-type=purchase-step1`;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const resp = await fetch(url, {
          method: method,
          headers: headers,
        });

        if (resp.status === 200) {
          return Promise.resolve(resp.json());
        } else {
          return Promise.resolve(undefined);
        }
      };
      const getDraftByTenant = async (tenantId: string) => {
        // console.debug('get draft by current tenant id');
        const accessToken = await getAccessTokenSilently();
        const method = 'GET';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/draft/tenants/${tenantId}?draft-type=purchase-step1`;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const resp = await fetch(url, {
          method: method,
          headers: headers,
        });

        if (resp.status === 200) {
          const drafts: FormData[] = await resp.json();
          const submittedDraft = drafts.find((draft) => draft.submitted);
          if (submittedDraft) {
            return Promise.resolve(submittedDraft);
          } else {
            return Promise.resolve(_.maxBy(drafts, 'timestamp'));
          }
        } else {
          return Promise.reject(
            new Error(
              `Failed in getting step 1 session data. status is ${resp.status}`,
            ),
          );
        }
      };

      const f = async () => {
        const cookieDraftId = getCookie(`Draft-${tenantId}`);
        setDraftId(cookieDraftId);
        const getDraftFn = cookieDraftId
          ? () => getDraftById(cookieDraftId)
          : () => getDraftByTenant(tenantId);

        setWaitingStates((prevState) => ({
          ...prevState,
          getInitData: true,
        }));
        try {
          const draft = await getDraftFn();
          if (draft) {
            // console.debug('get draft data, submitted:', draft.submitted);
            setDraftId(draft.applicationDraftId);
            if (draft.submitted) {
              setSessionData({});
              setHasSubmittedDraft(true);
              alert('有料プランご登録済です。');
              document.cookie = `Draft-${tenantId}=; path=/; max-age=0`;
            } else {
              setSessionData(JSON.parse((draft.jsonData as string) ?? '{}'));
            }
          } else {
            // console.debug('no draft');
            setDraftId(undefined);
            setSessionData({});
          }
        } catch {
          // todo: error handling
          setSessionData({});
        }
      };
      f();
    }
  }, [
    applicationControlAPIBaseUrl,
    customerId,
    getAccessTokenSilently,
    tenantId,
  ]);

  React.useEffect(() => {
    if (loadSessionData != null) {
      const getCustomerInfo = async (): Promise<CustomerInfo> => {
        const accessToken = await getAccessTokenSilently();
        const method = 'GET';
        const url = `${customerAPIBaseUrl}/api/customer/customers/${customerId}`;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const resp = await fetch(url, {
          method: method,
          headers: headers,
        });

        if (resp.status === 200) {
          return Promise.resolve(resp.json());
        } else {
          return Promise.reject(
            new Error(
              `Failed in getting customer info. status is ${resp.status}`,
            ),
          );
        }
      };
      const f = async () => {
        try {
          if (loadSessionData && sessionData) {
            setFormData((prev) => ({ ...prev, ...sessionData }));
            setLicenseNumber(
              (sessionData['service-license-number'] ?? '20') as string,
            );
            // start getting price info after session data set
            setInitDataLoaded(true);
          } else {
            // start getting price info right away
            // because init data won't change license number
            setInitDataLoaded(true);
            const customerInfo = await getCustomerInfo();
            const userInfo = customerInfo.users.find(
              (user: CustomerUser) => user.userId === userId,
            );
            const initData = {
              'contact-person-last-name': userInfo?.userLastName,
              'contact-person-first-name': userInfo?.userFirstName,
              'contact-person-last-name-kana': userInfo?.userLastNameKana,
              'contact-person-first-name-kana': userInfo?.userFirstNameKana,
              'contact-person-department': userInfo?.department,
              'contact-person-title': userInfo?.jobTitle,
              'contact-person-tel': userInfo?.tel,
              'organization-name': customerInfo.company.companyName,
            };
            setFormData((prev) => ({ ...prev, ...initData }));
          }
          setWaitingStates((prevState) => ({
            ...prevState,
            getInitData: false,
          }));
        } catch {
          setWaitingStates((prevState) => ({
            ...prevState,
            getInitData: false,
          }));
          setInitDataLoaded(true);
        }
      };
      f();
    }
  }, [
    customerAPIBaseUrl,
    customerId,
    getAccessTokenSilently,
    loadSessionData,
    sessionData,
    userId,
  ]);
  const [tenantName, setTenantName] = React.useState('');
  const [pjbUrl, setPjbUrl] = React.useState('');

  React.useEffect(() => {
    if (tenantId) {
      const f = async () => {
        const accessToken = await getAccessTokenSilently();
        const userResp = await fetch(
          `${tenantManagementAPIBaseUrl}/api/tenant-management/tenants/${tenantId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
        const tenantJson = await userResp.json();
        const tenantNameTmp = tenantJson?.[0]?.tenantName;
        setTenantName(tenantNameTmp);
        setPjbUrl(`https://${tenantName}${pjbDomainUrlSuffix}`);
      };
      f();
    }
  }, [
    getAccessTokenSilently,
    pjbDomainUrlSuffix,
    tenantId,
    tenantManagementAPIBaseUrl,
    tenantName,
  ]);

  const [nextTimeoutId, setNextTimeoutId] = React.useState<
    number | undefined
  >();

  const getUsage = React.useCallback(
    async (currentContractTenantId): Promise<UsageInfo> => {
      const accessToken = await getAccessTokenSilently();
      const method = 'GET';
      const url = `${applicationControlAPIBaseUrl}/api/application-controller/tenants/${currentContractTenantId}/usages`;
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      const resp = await fetch(url, {
        method: method,
        headers: headers,
      });

      if (resp.status === 200) {
        return Promise.resolve(resp.json());
      } else {
        return Promise.reject(
          new Error(`Failed in getting usage data. status is ${resp.status}`),
        );
      }
    },
    [applicationControlAPIBaseUrl, getAccessTokenSilently],
  );

  const [usedLicenseNumber, setUsedLicenseNumber] = React.useState(0);
  const setLicenseInfo = React.useCallback((usageInfo: { usage: Usage[] }) => {
    const productUsage = usageInfo?.usage?.find(
      (it) => it.resource === 'VALID_ACCOUNT_COUNT',
    );
    if (productUsage) {
      setUsedLicenseNumber(productUsage.value);
      setFormData((prev) => {
        const crr = _.cloneDeep(prev);
        crr['service-license-number-used'] = productUsage.value.toString();
        return crr;
      });
    }
  }, []);

  const updateLicenseInfo = React.useCallback(async () => {
    if (tenantId) {
      const res = await getUsage(tenantId);
      setLicenseInfo(res);
    }
  }, [getUsage, setLicenseInfo, tenantId]);
  const [updateUsageRequested, setUpdateUsageRequested] = React.useState(false);
  React.useEffect(() => {
    if (tenantId && tenantName && !updateUsageRequested) {
      setUpdateUsageRequested(true);
      const requestUpdateUsage = async (
        tenantId: string,
        tenantName: string,
      ) => {
        const accessToken = await getAccessTokenSilently();
        const method = 'POST';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/messages`;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const _body = {
          targetService: 'pjb',
          tenantId: tenantId,
          tenantName: tenantName,
          eventType: 'read',
          resource: 'usage',
        };
        const resp = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(_body),
        });

        if (resp.status === 201) {
          return Promise.resolve();
        } else {
          return Promise.reject(
            new Error(`Failed in getting usage data. status is ${resp.status}`),
          );
        }
      };

      setWaitingStates((prevState) => ({
        ...prevState,
        updateLicenseInfo: true,
      }));
      updateLicenseInfo().finally(() =>
        setWaitingStates((prevState) => ({
          ...prevState,
          updateLicenseInfo: false,
        })),
      );

      requestUpdateUsage(tenantId, tenantName);
      window.setTimeout(updateLicenseInfo, 5000);

      const requestUpdateUsageWithTimeout = (timeout: number) => {
        const timeoutId = window.setTimeout(() => {
          requestUpdateUsage(tenantId, tenantName);
          window.setTimeout(updateLicenseInfo, 5000);

          requestUpdateUsageWithTimeout(timeout * 2);
        }, timeout);
        setNextTimeoutId(timeoutId);
      };
      requestUpdateUsageWithTimeout(3 * 60000);
    }
    return () => {
      if (nextTimeoutId != null) {
        window.clearTimeout(nextTimeoutId);
      }
    };
  }, [
    applicationControlAPIBaseUrl,
    getAccessTokenSilently,
    getUsage,
    nextTimeoutId,
    setLicenseInfo,
    tenantId,
    tenantName,
    updateLicenseInfo,
    updateUsageRequested,
  ]);

  const handleClick = React.useCallback(async () => {
    // disable button to prevent multiple-submit
    setDisableSubmit(true);
    setSubmitForm(true);
  }, []);
  const formComponentsPart1Init: FormComponentProps[][] = React.useMemo(
    () => [
      [
        {
          kind: 'text-section-title',
          id: 'title-service',
          label: 'お申込み対象',
        },
      ],
      [
        {
          kind: 'input-normal',
          id: 'service-product',
          label: '利用サービス',
          defaultValue: serviceName('pjb'),
          size: 'medium',
          disabled: true,
        },
      ],
      [
        {
          kind: 'input-normal',
          id: 'service-license-number-used',
          label: '利用中のライセンス数',
          defaultValue: '1',
          size: 'medium',
          disabled: true,
        },
      ],
      [
        {
          kind: 'input-normal',
          id: 'service-license-number',
          label: 'お申込みライセンス数',
          required: true,
          defaultValue: '20',
          size: 'medium',
          postChange: (value) => setLicenseNumber(value),
        },
      ],
    ],
    [],
  );

  props.formComponentsPart2 = [
    [
      {
        kind: 'text-section-title',
        id: 'title-contact-person',
        label: 'ご担当者情報',
      },
    ],
    [
      {
        kind: 'input-email',
        id: 'contact-person-mail',
        label: 'メールアドレス',
        name: 'email',
        defaultValue: emailAddress,
        required: true,
        size: 'medium',
        disabled: true,
      },
    ],
    [
      {
        kind: 'input-normal',
        id: 'contact-person-last-name',
        label: '姓',
        name: 'last-name',
        required: true,
        size: 'medium',
      },
      {
        kind: 'input-normal',
        id: 'contact-person-first-name',
        label: '名',
        name: 'first-name',
        required: true,
        size: 'medium',
      },
    ],
    [
      {
        kind: 'input-normal',
        id: 'contact-person-last-name-kana',
        label: '姓（フリガナ）',
        name: 'last-name-kana',
        size: 'medium',
      },
      {
        kind: 'input-normal',
        id: 'contact-person-first-name-kana',
        label: '名（フリガナ）',
        name: 'first-name-kana',
        size: 'medium',
      },
    ],
    [
      {
        kind: 'input-normal',
        id: 'contact-person-department',
        label: '所属',
        name: 'contact-person-department',
        required: customerKind === 'corporate',
        size: 'medium',
      },
      {
        kind: 'input-normal',
        id: 'contact-person-title',
        label: '役職',
        name: 'contact-person-title',
        required: customerKind === 'corporate',
        size: 'medium',
      },
    ],
    [
      {
        kind: 'input-tel',
        id: 'contact-person-tel',
        label: '携帯番号 (テレワーク中の方など連絡が取れる番号)',
        name: 'tel',
        required: true,
        size: 'medium',
      },
    ],
    [
      {
        kind: 'text-section-title',
        id: 'title-organization',
        label: '会社・団体情報',
      },
    ],
    [
      {
        kind: 'input-normal',
        id: 'organization-name',
        label: '会社・団体名',
        name: 'organization',
        size: 'medium',
        required: customerKind === 'corporate',
      },
    ],
    [
      {
        kind: 'input-zipcode',
        id: 'input-zipcode',
        label: '郵便番号',
        required: customerKind === 'corporate',
        size: 'short',
        baseUrl: zipcodeAPIBaseUrl,
        postSelect: () => {
          setWarning(true);
          setIsValids &&
            setIsValids((prev) => ({
              ...prev,
              'select-prefecture': true,
              // 'input-address': true,
            }));
        },
      },
      {
        kind: 'select-prefecture',
        id: 'select-prefecture',
        label: '都道府県',
        required: customerKind === 'corporate',
        size: 'short',
        associatedId: 'input-zipcode',
        associatedProperty: 'prefecture',
      },
    ],
    [
      {
        kind: 'input-normal',
        id: 'input-address',
        label: '所在地',
        required: customerKind === 'corporate',
        size: 'full',
        warning: warning,
        helperText: '住所の続きを入力してください。',
        associatedId: 'input-zipcode',
        associatedProperty: 'address',
        onChange: () => {
          setWarning(false);
        },
      },
    ],
    [
      {
        kind: 'input-tel',
        id: 'organization-tel',
        label: '電話番号',
        name: 'tel',
        required: customerKind === 'corporate',
        size: 'medium',
      },
    ],
    [
      {
        kind: 'checkbox-normal',
        id: 'agreement',
        label: (
          <>
            <Link href="https://bizapp.worksap.co.jp/terms" target={'_blank'}>
              利用規約
            </Link>
            および
            <Link href="https://bizapp.worksap.co.jp/privacy" target={'_blank'}>
              個人情報の取り扱い
            </Link>
            について同意する。
          </>
        ),
        required: true,
        helperText: 'ご同意をお願いします。',
        className: classes.agreement,
      },
    ],
    [
      {
        kind: 'button-normal',
        id: 'submit-button',
        label: '次へ',
        className: classes.submitButton,
        disabled: disableSubmit || hasSubmittedDraft,
        preClick: updateLicenseInfo,
        postClick: handleClick,
      },
    ],
  ];

  const kinds = _.flatMapDeep(
    _.concat(formComponentsPart1Init, props.formComponentsPart2),
  )
    .filter((v) => !(v.kind.startsWith('text') || v.kind.startsWith('button')))
    .map((v) => v);

  const [formData, setFormData] = React.useState<FormData>(
    initFormDataPairs(kinds),
  );

  const [isValids, setIsValids] = React.useState(
    _.fromPairs(kinds.map((v) => [v.id, false])),
  );
  const [forceValidation, setForceValidation] = React.useState(false);

  const [waitingStates, setWaitingStates] = React.useState<{
    [key: string]: boolean;
  }>({});

  // check multi async webapi call to set loading spinner
  React.useEffect(() => {
    if (dialogConfig.state !== 'error') {
      if (_.some(_.values(waitingStates))) {
        setDialogConfig({ state: 'wait' });
      } else {
        setDialogConfig({ state: 'close' });
      }
    }
  }, [dialogConfig.state, waitingStates]);

  React.useEffect(() => {
    if (initDataLoaded) {
      let _licenseNumber = parseInt(licenseNumber, 10);
      if (_.isNaN(_licenseNumber)) {
        _licenseNumber = 0;
      }
      const getPrice = async () => {
        const method = 'POST';
        const url = `${feeCalculatorAPIBaseUrl}/fee-calculator`;
        const accessToken = await getAccessTokenSilently();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };

        const currentDate = new Date();
        const usageStartDate = addDays(currentDate, 1);
        const usageEndDate = endOfMonth(usageStartDate);
        //formats to 'yyyy-mm-dd'
        const formatter = Intl.DateTimeFormat('fr-CA', {
          timeZone: 'Asia/Tokyo',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        });

        const _body = {
          unitPrice: '960',
          quantity: _licenseNumber.toString(),
          usageStartDate: formatter.format(usageStartDate),
          usageEndDate: formatter.format(usageEndDate),
          closingDate: formatter.format(usageEndDate),
          billType: 'monthly',
          productType: ['by-the-day'],
        };

        const resp = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(_body),
        });

        if (resp.status !== 200) {
          return Promise.reject(
            new Error(`Failed in getting price data. status is ${resp.status}`),
          );
        }

        const jsonData = await resp.json();
        //When user opens form on the last day of month
        if (usageStartDate.getMonth() !== endOfMonth(currentDate).getMonth()) {
          return Promise.resolve({
            discountedPrice: {
              ...jsonData.discountedPrice,
              unitPriceIncludingTax: '0',
              priceIncludingTax: '0',
              discountAmountForUnitPriceIncludingTax:
                jsonData.discountedPrice.unitPriceIncludingTax,
            },
            normalPrice: {
              ...jsonData.normalPrice,
            },
          });
        } else {
          return Promise.resolve(jsonData);
        }
      };
      const f = async () => {
        try {
          setWaitingStates((prevState) => ({ ...prevState, getPrice: true }));
          const priceInfo = await getPrice();
          setPrice({ ...priceInfo, licenseNumber: _licenseNumber.toString() });
          setWaitingStates((prevState) => ({ ...prevState, getPrice: false }));
        } catch {
          setDialogConfig({ state: 'error' });
        }
      };
      f();
    }
  }, [
    feeCalculatorAPIBaseUrl,
    getAccessTokenSilently,
    initDataLoaded,
    licenseNumber,
    planId,
    serviceId,
  ]);

  const combineNames = (last?: string, first?: string) => {
    return (last ?? '') + (last && first ? '　' : '') + (first ?? '');
  };

  React.useEffect(() => {
    if (submitForm) {
      setSubmitForm(false);
      const createDraft = async () => {
        const method = 'POST';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/draft/tenants/${tenantId}`;
        const accessToken = await getAccessTokenSilently();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const _body = {
          applicationDraftType: 'purchase-step1',
          userKind: 'customer',
          userId: userId,
          customerId: customerId,
          saasBillingAddressId: globalState.saasBillingAddressId,
          tenantId: tenantId,
          serviceId: serviceId,
          notificationKind: 'email',
          jsonData: JSON.stringify({
            ...formData,
            'contact-person-name': combineNames(
              formData['contact-person-last-name'] as string,
              formData['contact-person-first-name'] as string,
            ),
            'contact-person-name-kana': combineNames(
              formData['contact-person-last-name-kana'] as string,
              formData['contact-person-first-name-kana'] as string,
            ),
            priceInfo: price,
            'plan-id': planId,
            'usage-kind': 'purchased',
            'contract-kind': 'volume',
            customerKind: customerKind,
          }),
        };

        const resp = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(_body),
        });

        return resp.status === 201
          ? Promise.resolve(resp.text())
          : Promise.reject(
              new Error(`Failed in submitting form. status is ${resp.status}`),
            );
      };
      const updateDraft = async (draftId: string) => {
        const method = 'PUT';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/draft/customers/${customerId}/drafts/${draftId}`;
        const accessToken = await getAccessTokenSilently();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const _body = {
          applicationDraftType: 'purchase-step1',
          saasBillingAddressId: globalState.saasBillingAddressId,
          jsonData: JSON.stringify({
            ...formData,
            priceInfo: price,
            'plan-id': planId,
            'usage-kind': 'purchased',
            'contract-kind': 'volume',
            customerKind: customerKind,
          }),
        };

        const resp = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(_body),
        });

        return resp.status === 200
          ? Promise.resolve(resp.text())
          : Promise.reject(
              new Error(`Failed in submitting form. status is ${resp.status}`),
            );
      };

      const f = async () => {
        try {
          setWaitingStates((prevState) => ({
            ...prevState,
            submitSessionData: true,
          }));

          if (draftId) {
            await timeout(updateDraft(draftId));
            document.cookie = `Draft-${tenantId}=${draftId}; path=/; max-age=5184000`;
          } else {
            const newDraftId = await timeout(createDraft());
            document.cookie = `Draft-${tenantId}=${newDraftId}; path=/; max-age=5184000`;
          }

          setWaitingStates((prevState) => ({
            ...prevState,
            submitSessionData: false,
          }));
          setStep(step + 1);
        } catch (err) {
          setDisableSubmit(false);
          setDialogConfig({ state: 'error' });
        }
      };
      f();
    } else {
      setSubmitForm(false);
      setDisableSubmit(false);
    }
  }, [
    applicationControlAPIBaseUrl,
    classes.pjbLink,
    customerId,
    customerKind,
    draftId,
    formData,
    getAccessTokenSilently,
    getUsage,
    licenseNumber,
    pjbUrl,
    planId,
    price,
    serviceId,
    setStep,
    step,
    submitForm,
    tenantId,
    userId,
    globalState.saasBillingAddressId,
  ]);

  const handleLoadSessionData = async (load: boolean) => {
    setLoadSessionData(load);
    setShowConfirmationDialog(false);
    if (!load && draftId) {
      // delete draft
      const deleteDraft = async (draftId: string) => {
        const method = 'DELETE';
        const url = `${applicationControlAPIBaseUrl}/api/application-controller/draft/customers/${customerId}/drafts/${draftId}`;
        const accessToken = await getAccessTokenSilently();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        };
        const _body = {
          applicationDraftType: 'purchase-step1',
        };

        const resp = await fetch(url, {
          method: method,
          headers: headers,
          body: JSON.stringify(_body),
        });

        return resp.status === 200
          ? Promise.resolve(resp.text())
          : Promise.reject(
              new Error(`Failed in deleting form. status is ${resp.status}`),
            );
      };

      const f = async () => {
        try {
          setWaitingStates((prevState) => ({
            ...prevState,
            deleteDraft: true,
          }));
          await timeout(deleteDraft(draftId));
          document.cookie = `Draft-${tenantId}=; path=/; max-age=0`;
          setDraftId(undefined);
        } catch (err) {
          setDialogConfig({ state: 'error' });
        } finally {
          setWaitingStates((prevState) => ({
            ...prevState,
            deleteDraft: false,
          }));
        }
      };
      f();
    }
  };

  const [formComponentsPart1, setFormComponentsPart1] = React.useState<
    FormComponentProps[][]
  >(formComponentsPart1Init);
  React.useEffect(() => {
    const licenseNumberIndex = formComponentsPart1Init.findIndex(
      (config) => config[0].id === 'service-license-number',
    );
    if (licenseNumberIndex < 0) return;
    const licenseNumberStr = (formData['service-license-number'] ??
      '') as string;

    const notValidInput = (value: string) =>
      value.match(new RegExp(/^[1-9][\d]*$/)) === null;

    const shouldValidInputHelperText = '1以上の数字を入力してください。';
    const shouldGreaterThanOrEqualsToUsedNumberHelperText =
      '現在ご利用数以上のライセンスをご購入ください。';

    const validate = (value: string): string => {
      const licenseNumber = +value;
      if (notValidInput(value)) {
        return shouldValidInputHelperText;
      } else if (usedLicenseNumber && usedLicenseNumber > licenseNumber) {
        return shouldGreaterThanOrEqualsToUsedNumberHelperText;
      } else {
        return '';
      }
    };

    setFormComponentsPart1((prev) => {
      const helperText = validate(licenseNumberStr);
      const curr = _.cloneDeep(prev);
      if (curr[licenseNumberIndex][0]) {
        curr[licenseNumberIndex][0] = {
          ...prev[licenseNumberIndex][0],
          error: helperText !== '',
          validator: {
            helperText: helperText,
            validate: () => validate(licenseNumberStr) === '',
          },
        } as FormComponentProps;
      }
      return curr;
    });
  }, [formComponentsPart1Init, formData, usedLicenseNumber]);

  return (
    <PurchaseApplicationStep1
      {...props}
      formComponentsPart1={formComponentsPart1}
      formData={formData}
      priceInfo={price}
      setFormData={setFormData}
      isValids={isValids}
      setIsValids={setIsValids}
      forceValidation={forceValidation}
      setForceValidation={setForceValidation}
      iconImgSrc={serviceIcon('pjb')}
      iconImgAlt="Project Board Logo"
      colors={{
        firstThemeColor: '#6A8AFF',
        secondThemeColor: '#6AA4FF',
      }}
      dialogConfig={dialogConfig}
      onClickDialogClose={handleDialogClick}
      showConfirmationDialog={showConfirmationDialog}
      onLoadSessionData={handleLoadSessionData}
    >
      <Link
        onClick={() => {
          navigate(-1);
        }}
        className={classes.backlink}
      >
        {'前のページへ戻る'}
      </Link>
    </PurchaseApplicationStep1>
  );
};
